import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTopmostEscape } from '../../../hooks/useTopmostEscape';
import { scrollParent } from '../useTableOffset';

/**
 * The side panel of /strategic-kpis (masterplan skpi-redesign §2 rule 6, §4 «Page-Root»; package A2a).
 *
 * A column of the page root, not a modal: 400 px to the right of the content (the page sets `.panel-open`
 * on `.skpi.skpi-root` while it renders this), no scrim, the content beside it stays usable. It is sticky in
 * the Layout's <main> scroller, which starts below the NoticeBar, so `top: 0` is already under the bar;
 * its height is the viewport minus the scroller's top edge and the bottom gutter, measured here and handed
 * to the kit as `--panel-offset` (the scroller moves when a notice is dismissed, hence the observer). Below
 * 1280 px the kit turns it into a sheet from the right edge, still without a scrim. Only the body scrolls.
 *
 * Mounted = open. On mount focus goes to the heading; Escape (topmost overlay only) or × closes; on unmount
 * focus returns to the element that had it when the panel opened — or, when the panel's content changed
 * through a click outside it (another row), to that element (`contentKey`).
 *
 * No data logic: title, subtitle, body and footer come in as props.
 */

interface Props {
    title: ReactNode;
    sub?: ReactNode;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    /** Changes when the panel shows another thing (another KPI, another person): focus moves to the heading again. */
    contentKey?: string;
    /** px; overrides the measured `--panel-offset`. */
    offset?: number;
    closeLabel?: string;
    testId?: string;
}

/** The gap below the panel to the viewport edge (the scroller's p-6). */
const BOTTOM_GUTTER = 24;

/**
 * Focus hand-over between two panels (person -> evidence). React 18 runs the layout-effect cleanup of the
 * unmounting panel, then the layout effects of the new one, then the passive cleanup of the old one. So the
 * old panel parks its return target in `pending` (layout cleanup), the new panel adopts it when it has none of
 * its own, and the old passive cleanup restores focus only if nobody took it over. Pure parts are exported for tests.
 */
export interface FocusHandoff<T> { pending: T | null }

export function parkReturnTarget<T>(h: FocusHandoff<T>, target: T | null): void {
    if (target) h.pending = target;
}

/** The new panel's own return target wins; otherwise it adopts the parked one (and consumes it). */
export function adoptReturnTarget<T>(h: FocusHandoff<T>, own: T | null): T | null {
    if (own) return own;
    const taken = h.pending;
    h.pending = null;
    return taken;
}

/** Closing panel: focus goes back only when it is lost (body/none) or still inside the closing panel. */
export function shouldRestoreFocus(activeIsBodyOrNone: boolean, activeInsideOwnPanel: boolean): boolean {
    return activeIsBodyOrNone || activeInsideOwnPanel;
}

const handoff: FocusHandoff<HTMLElement> = { pending: null };

export function SidePanel({ title, sub, onClose, children, footer, contentKey, offset, closeLabel = 'Close panel', testId }: Props) {
    const panel = useRef<HTMLElement>(null);
    const heading = useRef<HTMLHeadingElement>(null);
    const returnTo = useRef<HTMLElement | null>(null);
    const headingId = useId();
    const [measured, setMeasured] = useState<number | null>(null);

    useTopmostEscape(true, onClose);

    // Focus in on open and on every new content; remember where it came from (outside the panel only).
    useLayoutEffect(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body && !panel.current?.contains(active)) returnTo.current = active;
        returnTo.current = adoptReturnTarget(handoff, returnTo.current);
        heading.current?.focus({ preventScroll: true });
    }, [contentKey]);

    // Unmount: park the return target for a panel mounting in the same commit (runs before its layout effects).
    const panelEl = useRef<HTMLElement | null>(null);
    useLayoutEffect(() => {
        panelEl.current = panel.current;
        return () => parkReturnTarget(handoff, returnTo.current);
    }, []);

    // Focus back on close (passive cleanup, after a successor panel has mounted and focused its heading).
    useEffect(() => () => {
        const target = returnTo.current;
        if (handoff.pending === target) handoff.pending = null; // nobody adopted it: a plain close
        const active = document.activeElement;
        const lost = !active || active === document.body;
        const inside = !!panelEl.current && !!active && panelEl.current.contains(active);
        if (target && target.isConnected && shouldRestoreFocus(lost, inside)) target.focus({ preventScroll: true });
    }, []);

    // --panel-offset: the scroller's top edge in the viewport plus the bottom gutter.
    useLayoutEffect(() => {
        if (offset !== undefined) return;
        const scroller = scrollParent(panel.current);
        const measure = () => {
            const top = scroller ? Math.max(0, scroller.getBoundingClientRect().top) : 0;
            setMeasured(Math.round(top + BOTTOM_GUTTER));
        };
        measure();
        window.addEventListener('resize', measure);
        const ro = scroller && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro && scroller) ro.observe(scroller);
        return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
    }, [offset]);

    const px = offset ?? measured;
    const style = px !== null ? ({ '--panel-offset': `${px}px` } as React.CSSProperties) : undefined;

    return (
        <aside ref={panel} className="skpi-panel" aria-labelledby={headingId} style={style} data-testid={testId}>
            <div className="skpi-panel-head">
                <div className="titles">
                    <h2 id={headingId} ref={heading} tabIndex={-1}>{title}</h2>
                    {sub && <p className="sub">{sub}</p>}
                </div>
                <button type="button" className="icon-btn sm" onClick={onClose} aria-label={closeLabel} data-testid="skpi-panel-close">
                    <X className="i" aria-hidden="true" />
                </button>
            </div>
            <div className="skpi-panel-body">{children}</div>
            {footer && <div className="skpi-panel-foot">{footer}</div>}
        </aside>
    );
}
